import { verticalCompactor } from "react-grid-layout/core";
import type { LayoutItem as GridItem } from "react-grid-layout/core";
import { GRID_COLUMNS } from "./layoutConfig";
import type { LayoutItem, SignalsDashboardLayoutV1 } from "./types";

/**
 * Converts between the layout's items and react-grid-layout's, so the rest of
 * the module and the stored document stay free of library vocabulary.
 */
export function toGridItem(item: LayoutItem): GridItem {
    return {
        i: item.objectId,
        x: item.x,
        y: item.y,
        w: item.width,
        h: item.height,
    };
}

export function fromGridItem(item: GridItem): LayoutItem {
    return {
        objectId: item.i,
        x: item.x,
        y: item.y,
        width: item.w,
        height: item.h,
    };
}

/**
 * Compacts upward with react-grid-layout's own compactor. The renderer
 * compacts every layout it is given, so using the same engine keeps a card
 * from moving after a drag ends. Item order is preserved.
 */
export function compactItems(
    items: readonly LayoutItem[],
    columns: number = GRID_COLUMNS,
): LayoutItem[] {
    if (items.length === 0) return [];

    const originalOrder = items.map((item) => item.objectId);
    const compacted = verticalCompactor.compact(items.map(toGridItem), columns);
    const compactedById = new Map(
        compacted.map((item) => [item.i, fromGridItem(item)]),
    );

    return originalOrder
        .map((objectId) => compactedById.get(objectId))
        .filter((item): item is LayoutItem => item !== undefined);
}

/**
 * Applies one changed item and compacts the result. The compactor resolves
 * collisions, so the changed item keeps its position and the rest settle
 * around it.
 */
export function applyItemChange(
    layout: SignalsDashboardLayoutV1,
    changedItem: LayoutItem,
): SignalsDashboardLayoutV1 {
    if (!layout.items.some((item) => item.objectId === changedItem.objectId)) {
        return layout;
    }

    const withChangedItem = layout.items.map((item) =>
        item.objectId === changedItem.objectId ? changedItem : item,
    );

    return {
        ...layout,
        items: compactItems(withChangedItem, layout.columns),
    };
}

/**
 * Places a card below everything else, which is where a newly added card goes.
 */
export function appendItem(
    items: readonly LayoutItem[],
    newItem: LayoutItem,
    columns: number = GRID_COLUMNS,
): LayoutItem[] {
    const bottom = items.reduce(
        (lowest, item) => Math.max(lowest, item.y + item.height),
        0,
    );

    return compactItems([...items, { ...newItem, x: 0, y: bottom }], columns);
}
